<template>
  <v-card
    class="premium-receituario-card"
    variant="flat"
  >
    <!-- Header -->
    <v-card-text class="premium-header px-4 py-3">
      <v-row
        no-gutters
        align="center"
        class="flex-nowrap toolbar-row"
      >
        <!-- Controle de Data -->
        <div class="date-controls d-flex align-center">
          <v-tooltip location="top">
            <template #activator="{ props }">
              <div
                v-bind="props"
                class="d-flex align-center"
              >
                <v-switch
                  v-model="dataHabilitada"
                  hide-details
                  color="grey-darken-2"
                  class="mr-2"
                  density="compact"
                />
                <span class="text-caption text-grey-darken-2">{{ dataHabilitada ? 'Data visível' : 'Data oculta' }}</span>
              </div>
            </template>
            <span>{{ dataHabilitada ? 'Data visível na prescrição' : 'Data oculta na prescrição' }}</span>
          </v-tooltip>
          
          <v-menu
            v-if="dataHabilitada"
            v-model="menuData"
            :close-on-content-click="false"
          >
            <template #activator="{ props }">
              <v-text-field
                v-model="dataFormatada"
                label="Data"
                density="compact"
                variant="outlined"
                color="grey-darken-3"
                prepend-inner-icon="calendar_today"
                readonly
                v-bind="props"
                hide-details
                class="data-field ml-4"
                style="width: 140px;"
              />
            </template>
            <v-date-picker 
              v-model="data" 
              no-title 
              locale="pt-BR"
              @update:model-value="menuData = false"
            />
          </v-menu>
        </div>

        <v-spacer />

        <!-- Seção de Modelos -->
        <div class="models-section d-flex align-center">
          <v-autocomplete
            v-model="modeloSelecionado"
            :items="modelos"
            :loading="carregandoModelos"
            density="compact"
            variant="outlined"
            hide-no-data
            color="grey-darken-3"
            item-title="nome"
            item-value="id"
            label="Buscar Modelos"
            placeholder="Digite para buscar"
            prepend-inner-icon="search"
            return-object
            hide-details
            class="model-autocomplete mr-2"
            style="min-width: 200px;"
          />
          
          <!-- Botões de Ação dos Modelos -->
          <div class="model-actions d-flex align-center gap-2">
            <v-menu location="bottom">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  variant="text"
                  class="model-action-btn"
                >
                  <v-icon>more_vert</v-icon>
                </v-btn>
              </template>
              <v-list density="compact">
                <v-list-item
                  :disabled="!modeloAtivo"
                  @click="limparModelo"
                >
                  <template #prepend>
                    <v-icon>cleaning_services</v-icon>
                  </template>
                  <v-list-item-title>Limpar modelo</v-list-item-title>
                </v-list-item>
                <v-list-item
                  @click="abrirDialogNovoModelo"
                >
                  <template #prepend>
                    <v-icon>note_add</v-icon>
                  </template>
                  <v-list-item-title>Novo modelo</v-list-item-title>
                </v-list-item>
                <v-list-item
                  :disabled="!modeloAtivo || !conteudoEditor || conteudoEditor.length < 8"
                  @click="abrirDialogEditarModelo"
                >
                  <template #prepend>
                    <v-icon>edit</v-icon>
                  </template>
                  <v-list-item-title>Editar modelo</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </v-row>
    </v-card-text>

    <v-divider class="border-grey-lighten-2" />

    <!-- Área do Editor -->
    <v-card-text class="editor-container pa-0">
      <div class="editor-wrapper pa-3">
        <tiptap-editor
          ref="editorRef"
          v-model="conteudoEditor"
          :placeholder="'Descreva a receita médica aqui...'"
          :editable="true"
          :min-height="'400px'"
          @update:modelValue="onEditorUpdate"
        />
      </div>
    </v-card-text>

    <v-dialog
      v-model="dialogAplicarModelo"
      width="420"
    >
      <v-card variant="outlined" style="background-color: rgb(var(--v-theme-surface)); color: rgb(var(--v-theme-on-surface));">
        <v-card-title>Aplicar modelo</v-card-title>
        <v-card-text>
          Escolha como deseja inserir o modelo
          <strong>{{ modeloPendente?.nome || '' }}</strong>.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="cancelarAplicacaoModelo">Cancelar</v-btn>
          <v-btn variant="outlined" @click="aplicarModeloSelecionado('substituir')">Substituir</v-btn>
          <v-btn color="primary" variant="flat" @click="aplicarModeloSelecionado('acrescentar')">Acrescentar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo para Novo Modelo -->
    <v-dialog
      v-model="dialogNovoModelo"
      width="700"
    >
      <v-card
        class="premium-dialog-card"
        variant="outlined"
        style="background-color: rgb(var(--v-theme-surface));"
      >
        <v-card-title class="premium-dialog-header py-3 d-flex align-center text-grey-darken-3">
          <v-icon
            color="grey-darken-3"
            class="mr-2"
          >
            add_circle
          </v-icon>
          <span class="dialog-title text-subtitle-1 font-weight-medium">Novo Modelo de Receita</span>
        </v-card-title>

        <v-divider class="border-grey-lighten-2" />

        <v-card-text class="dialog-content pa-4">
          <v-text-field
            v-model="tituloModelo"
            label="Título do Modelo"
            variant="outlined"
            color="grey-darken-3"
            :rules="[v => !!v || 'Título é obrigatório']"
            prepend-inner-icon="title"
            density="compact"
            class="mb-3"
          />
          
          <div class="dialog-editor-container">
            <div class="editor-label mb-2">
              <span class="label-text text-caption text-grey-darken-2">Conteúdo do modelo:</span>
            </div>
            <tiptap-editor
              ref="editorModeloRef"
              v-model="conteudoEditorModelo"
              :placeholder="'Digite o conteúdo do modelo de receita...'"
              :editable="true"
              :min-height="'250px'"
            />
          </div>
        </v-card-text>

        <v-divider class="border-grey-lighten-2" />

        <v-card-actions class="dialog-actions pa-3">
          <v-spacer />
          <v-btn 
            color="grey-darken-2" 
            variant="outlined" 
            class="action-btn cancel-btn" 
            size="small"
            @click="dialogNovoModelo = false"
          >
            <v-icon left>
              close
            </v-icon>
            Cancelar
          </v-btn>
          <v-btn 
            color="grey-darken-3"
            variant="flat" 
            :disabled="!tituloModelo || !conteudoEditorModelo"
            :loading="salvandoModelo"
            class="action-btn save-model-btn"
            size="small"
            @click="salvarModelo"
          >
            <v-icon left>
              check
            </v-icon>
            Criar Modelo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo para Editar Modelo -->
    <v-dialog
      v-model="dialogEditarModelo"
      width="700"
    >
      <v-card
        class="premium-dialog-card"
        variant="outlined"
        style="background-color: rgb(var(--v-theme-surface));"
      >
        <v-card-title class="premium-dialog-header py-3 d-flex align-center text-grey-darken-3">
          <v-icon
            color="grey-darken-3"
            class="mr-2"
          >
            edit
          </v-icon>
          <span class="dialog-title text-subtitle-1 font-weight-medium">Editar Modelo</span>
        </v-card-title>

        <v-divider class="border-grey-lighten-2" />

        <v-card-text class="dialog-content pa-4">
          <v-text-field
            v-model="tituloModelo"
            label="Título do Modelo"
            variant="outlined"
            color="grey-darken-3"
            :rules="[v => !!v || 'Título é obrigatório']"
            prepend-inner-icon="title"
            density="compact"
            class="mb-3"
          />
          
          <div class="dialog-editor-container">
            <div class="editor-label mb-2">
              <span class="label-text text-caption text-grey-darken-2">Conteúdo do modelo:</span>
            </div>
            <tiptap-editor
              ref="editorModeloRef"
              v-model="conteudoEditorModelo"
              :placeholder="'Digite o conteúdo do modelo de receita...'"
              :editable="true"
              :min-height="'250px'"
            />
          </div>
        </v-card-text>

        <v-divider class="border-grey-lighten-2" />

        <v-card-actions class="dialog-actions pa-3">
          <v-spacer />
          <v-btn 
            color="grey-darken-2" 
            variant="outlined" 
            class="action-btn cancel-btn" 
            size="small"
            @click="dialogEditarModelo = false"
          >
            <v-icon left>
              close
            </v-icon>
            Cancelar
          </v-btn>
          <v-btn 
            color="grey-darken-3"
            variant="flat" 
            :disabled="!tituloModelo || !conteudoEditorModelo"
            :loading="salvandoModelo"
            class="action-btn save-edit-btn"
            size="small"
            @click="salvarModelo"
          >
            <v-icon left>
              check
            </v-icon>
            Salvar Alterações
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para feedback -->
    <v-snackbar
      v-model="snackbar.visivel"
      :color="snackbar.cor"
      :timeout="3000"
      location="top"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">
          {{ snackbar.cor === 'success' ? 'check_circle' : 
            snackbar.cor === 'error' ? 'error' : 
            'info' }}
        </v-icon>
        {{ snackbar.mensagem }}
      </div>
    </v-snackbar>
  </v-card>
</template>

<script>
export default {
  name: 'PremiumReceituarioEditor'
}
</script>

<script setup>
import { ref, computed, watch, onMounted, defineProps, defineEmits, defineExpose } from 'vue';
import { useStore } from 'vuex';
import medicoService from '@/services/medico.service';
import TiptapEditor from '../editor/tiptapEditor.vue';

const props = defineProps({
  isEditarReceita: {
    type: Boolean,
    default: false
  },
  receitaEdit: {
    type: Object,
    default: () => ({})
  },
  ficha: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['cancelado', 'salvo']);

const store = useStore();
const usuario = computed(() => store.getters.usuario);

const conteudoEditor = ref('');
const conteudoEditorModelo = ref('');
const modeloSelecionado = ref(null);
const tituloModelo = ref('');
const dataHabilitada = ref(true);
const data = ref(new Date().toISOString().substr(0, 10));
const menuData = ref(false);
const dialogNovoModelo = ref(false);
const dialogEditarModelo = ref(false);
const dialogAplicarModelo = ref(false);
const modelos = ref([]);
const carregandoModelos = ref(false);
const salvando = ref(false);
const salvandoModelo = ref(false);
const modeloPendente = ref(null);
const modeloAtivo = ref(null);
const snackbar = ref({
  visivel: false,
  mensagem: '',
  cor: 'success'
});

const parseDateParts = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = String(value.getFullYear());
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return { y, m, d };
  }
  if (typeof value === 'number') {
    const dt = new Date(value);
    if (!Number.isNaN(dt.getTime())) {
      const y = String(dt.getFullYear());
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const d = String(dt.getDate()).padStart(2, '0');
      return { y, m, d };
    }
  }
  if (typeof value === 'string') {
    if (value.includes('-')) {
      const [y, m, d] = value.split('-');
      if (y && m && d) return { y, m, d };
    }
    if (value.includes('/')) {
      const [d, m, y] = value.split('/');
      if (y && m && d) return { y, m, d };
    }
  }
  return null;
};

const dataFormatada = computed(() => {
  const parts = parseDateParts(data.value);
  if (!parts) return '';
  return `${parts.d}/${parts.m}/${parts.y}`;
});

const normalizarDataReceita = (valor) => {
  const parts = parseDateParts(valor);
  if (!parts) return null;
  return `${parts.y}-${parts.m}-${parts.d}`;
};

const campoReceitaTemConteudo = () => {
  const texto = (conteudoEditor.value || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, '')
    .trim();
  return texto.length > 0;
};

watch(() => props.receitaEdit, (novaReceita) => {
  if (novaReceita && novaReceita.descricao) {
    conteudoEditor.value = novaReceita.descricao;
    if (Object.prototype.hasOwnProperty.call(novaReceita, 'dataHabilitada')) {
      dataHabilitada.value = !!novaReceita.dataHabilitada;
    } else if (Object.prototype.hasOwnProperty.call(novaReceita, 'dataSelecionada')) {
      dataHabilitada.value = novaReceita.dataSelecionada != null;
    }
    const dataReceita =
      novaReceita.dataPrescricao ||
      novaReceita.dataSelecionada ||
      novaReceita.dataReceita ||
      novaReceita.dataCadastro ||
      novaReceita.data ||
      null;
    const dataNormalizada = normalizarDataReceita(dataReceita);
    if (dataNormalizada) {
      data.value = dataNormalizada;
    }
  } else {
    conteudoEditor.value = '';
    data.value = new Date().toISOString().substr(0, 10);
    dataHabilitada.value = true;
  }
}, { immediate: true });

watch(modeloSelecionado, (novoModelo) => {
  if (novoModelo) {
    modeloPendente.value = novoModelo;
    if (!campoReceitaTemConteudo()) {
      aplicarModeloSelecionado('substituir');
    } else {
      dialogAplicarModelo.value = true;
      modeloSelecionado.value = null;
    }
  }
}, { deep: true });

const inicializar = () => {
  carregarModelos();
  if (props.receitaEdit && props.receitaEdit.descricao) {
    conteudoEditor.value = props.receitaEdit.descricao;
  }
};

const onEditorUpdate = (content) => {
  if (conteudoEditor.value !== content) {
    conteudoEditor.value = content;
  }
};

const carregarModelos = async () => {
  carregandoModelos.value = true;
  try {
    const resposta = await medicoService.medico.buscarModelosReceituario(usuario.value.id);
    if (resposta.status === 200) {
      modelos.value = resposta.data.modelos || [];
    }
  } catch (erro) {
    console.error('Erro ao carregar modelos:', erro);
    mostrarNotificacao('Erro ao carregar modelos', 'error');
  } finally {
    carregandoModelos.value = false;
  }
};

const limparModelo = () => {
  modeloSelecionado.value = null;
  modeloAtivo.value = null;
  tituloModelo.value = '';
  mostrarNotificacao('Modelo limpo com sucesso', 'info');
};

const aplicarModeloSelecionado = (modo) => {
  if (!modeloPendente.value) return;

  modeloAtivo.value = modeloPendente.value;
  tituloModelo.value = modeloPendente.value.nome;
  conteudoEditor.value = modo === 'substituir'
    ? modeloPendente.value.descricao
    : (conteudoEditor.value ? `${conteudoEditor.value}<p></p>${modeloPendente.value.descricao}` : modeloPendente.value.descricao);
  cancelarAplicacaoModelo();
};

const cancelarAplicacaoModelo = () => {
  dialogAplicarModelo.value = false;
  modeloPendente.value = null;
  modeloSelecionado.value = null;
};

const abrirDialogNovoModelo = () => {
  modeloSelecionado.value = null;
  tituloModelo.value = '';
  conteudoEditorModelo.value = conteudoEditor.value;
  dialogNovoModelo.value = true;
};

const abrirDialogEditarModelo = () => {
  if (modeloAtivo.value) {
    tituloModelo.value = modeloAtivo.value.nome;
    conteudoEditorModelo.value = modeloAtivo.value.descricao;
    dialogEditarModelo.value = true;
  }
};

const salvarModelo = async () => {
  salvandoModelo.value = true;
  try {
    const modelo = {
      idUsuario: usuario.value.id,
      nome: tituloModelo.value,
      descricao: conteudoEditorModelo.value,
      _id: dialogEditarModelo.value ? (modeloAtivo.value?._id || null) : null
    };
    
    const resposta = await medicoService.medico.cadastrarModeloReceituario(modelo);
    if (resposta.status === 200) {
      mostrarNotificacao('Modelo salvo com sucesso', 'success');
      dialogNovoModelo.value = false;
      dialogEditarModelo.value = false;
      carregarModelos();
    }
  } catch (erro) {
    console.error('Erro ao salvar modelo:', erro);
    mostrarNotificacao('Erro ao salvar modelo', 'error');
  } finally {
    salvandoModelo.value = false;
  }
};

const salvarReceita = async () => {
  if(!conteudoEditor.value || conteudoEditor.value.length < 5) {
      mostrarNotificacao('O conteúdo da receita é muito curto.', 'warning');
      return;
  }
  salvando.value = true;
  try {
    const dados = {
      idAtendimento: props.ficha.idAtendimento,
      idMedico: usuario.value.id,
      idPaciente: props.ficha?.idPaciente || null,
      descricao: conteudoEditor.value,
      dataSelecionada: dataHabilitada.value ? dataFormatada.value : null,
      dataHabilitada: dataHabilitada.value
    };
    
    let resposta;
    if (props.isEditarReceita && props.receitaEdit && props.receitaEdit.idReceitaMedica) {
      dados.idReceitaMedica = props.receitaEdit.idReceitaMedica;
      resposta = await medicoService.medico.atualizarReceitaMedica(dados);
    } else {
      resposta = await medicoService.medico.cadastrarReceitaMedica(dados);
    }

    if (resposta.status === 200) {
        mostrarNotificacao(`Receita ${props.isEditarReceita ? 'atualizada' : 'salva'} com sucesso`, 'success');
        emit('salvo');
    }

  } catch (erro) {
    console.error('Erro ao salvar receita:', erro);
    mostrarNotificacao('Erro ao salvar receita', 'error');
  } finally {
    salvando.value = false;
  }
};

const cancelar = () => {
  emit('cancelado');
};

const mostrarNotificacao = (mensagem, tipo = 'success') => {
  snackbar.value = {
    visivel: true,
    mensagem,
    cor: tipo === 'error' ? 'error' : tipo === 'warning' ? 'warning' : 'success'
 }
};

onMounted(inicializar);

defineExpose({
    salvarReceita,
    cancelar,
    limparModelo
});

</script>

<style scoped>
.premium-receituario-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Header */
.premium-header {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  min-height: 70px;
}

.toolbar-row {
  min-height: 48px;
}

/* Controles de Data */
.date-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.data-field {
  min-width: 140px;
}

/* Seção de Modelos */
.models-section {
  gap: 8px;
}

.model-autocomplete {
  min-width: 200px;
}

.model-autocomplete :deep(.v-field) {
  border-radius: 6px;
}

.model-action-btn {
  border-radius: 6px;
  transition: all 0.2s ease;
}

.model-action-btn:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.05);
  transform: scale(1.05);
}

/* Área do Editor */
.editor-container {
  flex: 1;
  max-height: 500px;
  min-height: 420px;
  overflow-y: auto;
}

.editor-wrapper {
  height: 100%;
  border-radius: 6px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

/* Diálogos */
.premium-dialog-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.premium-dialog-header {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 16px 20px;
}

.dialog-title {
  font-size: 1rem;
  font-weight: 500;
}

.dialog-content {
  max-height: 60vh;
  overflow-y: auto;
}

.dialog-editor-container {
  border-radius: 6px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  overflow: hidden;
}

.editor-label {
  display: block;
  padding: 8px 0;
}

.label-text {
  font-size: 0.875rem;
  font-weight: 500;
}

.dialog-actions {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.action-btn {
  text-transform: none;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.cancel-btn {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
}

.save-model-btn, .save-edit-btn {
  /* Cores definidas no componente */
}

.save-model-btn:hover, .save-edit-btn:hover {
  transform: translateY(-1px);
}

/* Responsividade */
@media (max-width: 960px) {
  .premium-header {
    padding: 8px 12px;
  }
  
  .date-controls {
    flex-direction: column;
    gap: 8px;
  }
  
  .data-field {
    width: 100%;
    min-width: auto;
    margin-left: 0 !important;
  }
  
  .models-section {
    flex-direction: column;
    gap: 8px;
  }
  
  .model-autocomplete {
    width: 100%;
    min-width: auto;
  }
}

@media (max-width: 600px) {
  .premium-header {
    padding: 6px 8px;
  }
  
  .toolbar-row {
    flex-wrap: wrap;
  }
  
  .dialog-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .dialog-actions .v-btn {
    width: 100%;
  }
}
</style>
